import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, ArrowRight } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #474545;
  padding: 100px 20px 60px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #FAECEC;
  opacity: 0.8;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(motion.article)`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.08) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 151, 178, 0.2);
    border-color: rgba(0, 151, 178, 0.4);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.src 
    ? `url(${props.src}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #0097B2 0%, #007a94 100%)'};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(71, 69, 69, 0.9) 0%, transparent 100%);
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #0097B2;
  font-size: 0.85rem;
  font-weight: 500;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #FAECEC;
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardDescription = styled.p`
  color: #FAECEC;
  opacity: 0.75;
  font-size: 0.95rem;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    gap: 12px;
    color: #FAECEC;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
`;

const EmptyIcon = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.2) 0%, rgba(0, 151, 178, 0.1) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  color: #FAECEC;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  color: #FAECEC;
  opacity: 0.7;
  max-width: 400px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const LoadingSpinner = styled(motion.div)`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(0, 151, 178, 0.2);
  border-top-color: #0097B2;
  border-radius: 50%;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 16px;
  color: #FAECEC;
`;

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

const estimateReadTime = (html) => {
  if (!html) return '3 min';
  const text = html.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min`;
};

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles`);
        if (!response.ok) {
          throw new Error('Failed to fetch articles');
        }
        const data = await response.json();
        setArticles(data.articles || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <LoadingContainer>
            <LoadingSpinner
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </LoadingContainer>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackLink to="/" data-testid="back-to-home">
          <ArrowLeft size={20} />
          Back to Home
        </BackLink>

        <Header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title data-testid="blog-title">Blog</Title>
          <Subtitle>
            Insights, tutorials, and updates about AI trading, automation, and financial strategies
          </Subtitle>
        </Header>

        {error ? (
          <ErrorMessage data-testid="error-message">
            <p>Unable to load articles. Please try again later.</p>
          </ErrorMessage>
        ) : articles.length === 0 ? (
          <EmptyState data-testid="empty-state">
            <EmptyIcon>
              <Calendar size={40} color="#0097B2" />
            </EmptyIcon>
            <EmptyTitle>No articles yet</EmptyTitle>
            <EmptyText>
              Stay tuned! We're working on exciting content about AI trading and automation.
            </EmptyText>
          </EmptyState>
        ) : (
          <BlogGrid data-testid="blog-grid">
            {articles.map((article, index) => (
              <BlogCard
                key={article.slug}
                data-testid={`blog-card-${article.slug}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CardImage src={article.heroImageUrl} />
                <CardContent>
                  <CardMeta>
                    <MetaItem>
                      <Calendar size={14} />
                      {formatDate(article.createdAt)}
                    </MetaItem>
                    <MetaItem>
                      <Clock size={14} />
                      {estimateReadTime(article.content_html)}
                    </MetaItem>
                  </CardMeta>
                  <CardTitle>{article.title}</CardTitle>
                  <CardDescription>{article.metaDescription}</CardDescription>
                  <ReadMoreLink 
                    to={`/blog/${article.slug}`}
                    data-testid={`read-more-${article.slug}`}
                  >
                    Read More <ArrowRight size={16} />
                  </ReadMoreLink>
                </CardContent>
              </BlogCard>
            ))}
          </BlogGrid>
        )}
      </Container>
    </PageContainer>
  );
};

export default Blog;
